import { type ReactNode } from 'react';
import { HelpTip } from '@/components/ui/help-tip';
import { useRaceBonusCurrent } from '../hooks/use-race-bonus-current';
import type { RaceBonusEntry } from '../hooks/use-race-bonus-current';
import { RaceActive } from './race-chip-active';
import { RaceWinner } from './race-chip-winner';
import { RaceCycleLabel } from './race-cycle-label';

const HELP =
  'El primer collector que alcance el objetivo de cada ciclo (diario, semanal, mensual) gana el premio. Solo uno gana por ciclo.';

function Header({ headerRight }: { headerRight?: ReactNode }): ReactNode {
  return (
    <div className="flex items-start justify-between gap-sm">
      <p className="text-[10px] font-medium uppercase tracking-wider text-text-tertiary/60">
        Bonus de velocidad
        <span className="ml-0.5 inline-block normal-case">
          <HelpTip text={HELP} />
        </span>
      </p>
      {headerRight}
    </div>
  );
}

function Amount({ race }: { race: RaceBonusEntry }): ReactNode {
  return (
    <p className="flex-shrink-0 whitespace-nowrap font-mono text-sm font-bold leading-none tabular-nums text-text-primary">
      ${race.amount_eur}
    </p>
  );
}

function CycleRow({ race }: { race: RaceBonusEntry }): ReactNode {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-sm">
        <RaceCycleLabel race={race} />
        <Amount race={race} />
      </div>
      {race.winner ? <RaceWinner race={race} /> : <RaceActive race={race} />}
    </div>
  );
}

function Body({ races }: { races: RaceBonusEntry[] }): ReactNode {
  return (
    <div className="mt-xs divide-y divide-white/[0.04]">
      {races.map((race) => (
        <div key={race.cycle_type} className="py-sm first:pt-0 last:pb-0">
          <CycleRow race={race} />
        </div>
      ))}
    </div>
  );
}

type CardProps = { headerRight?: ReactNode };

export function RaceChipCard({ headerRight }: CardProps = {}): ReactNode {
  const { data } = useRaceBonusCurrent();
  const races = data?.races ?? [];
  if (races.length === 0) return null;
  return (
    <div className="flex flex-col rounded-xl border border-white/[0.04] bg-white/[0.02] p-base">
      <Header {...(headerRight && { headerRight })} />
      <Body races={races} />
    </div>
  );
}
